<template>
  <div class="category-management-container">
    <!-- 权限检查 -->
    <div v-if="!isAdminLoggedIn" class="permission-denied">
      <el-alert
        title="权限不足"
        description="请先登录管理员账号"
        type="error"
        show-icon
        :closable="false"
      >
        <template slot="default">
          <el-button type="primary" @click="goToAdminLogin">前往登录</el-button>
        </template>
      </el-alert>
    </div>

    <!-- 主要内容区域 -->
    <div v-else>
      <div class="management-header">
        <h2>分类管理</h2>
        <div class="header-actions">
          <!-- 新文科模块的按钮 -->
          <template v-if="currentModule === 'xinwenke'">
            <el-dropdown
              v-if="canFileManage"
              size="small"
              trigger="click"
              @command="handleXinwenkeCreate"
              class="xinwenke-add-dropdown"
            >
              <el-button type="primary" icon="el-icon-plus" size="small">
                新增
                <i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="category">新增类别</el-dropdown-item>
                <el-dropdown-item command="type">新增类型</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
          <!-- 其他模块的按钮 -->
          <template v-else>
            <el-button 
              type="primary" 
              icon="el-icon-plus" 
              @click="showAddTagDialog"
              v-if="canFileManage"
              size="small"
            >
              新增
            </el-button>
          </template>
          <el-button 
            icon="el-icon-refresh" 
            @click="loadData"
            :loading="loading"
            size="small"
          >
            刷新
          </el-button>
          <el-button 
            icon="el-icon-switch-button" 
            @click="handleLogout"
            type="danger"
            size="small"
          >
            退出登录
          </el-button>
        </div>
      </div>

      <!-- 模块切换按钮 -->
      <div class="module-tabs">
        <el-button-group>
          <el-button 
            :type="currentModule === 'internet' ? 'primary' : 'default'"
            @click="switchModule('internet')"
          >
            创新大赛
          </el-button>
          <el-button 
            :type="currentModule === 'dachallenge' ? 'primary' : 'default'"
            @click="switchModule('dachallenge')"
          >
            大挑
          </el-button>
          <el-button 
            :type="currentModule === 'xiaochallenge' ? 'primary' : 'default'"
            @click="switchModule('xiaochallenge')"
          >
            小挑
          </el-button>
          <el-button 
            :type="currentModule === 'career' ? 'primary' : 'default'"
            @click="switchModule('career')"
          >
            职业大赛
          </el-button>
          <el-button 
            :type="currentModule === 'xinwenke' ? 'primary' : 'default'"
            @click="switchModule('xinwenke')"
          >
            新文科大赛
          </el-button>
        </el-button-group>
      </div>

      <!-- 新文科模块：分类管理 -->
      <div v-if="currentModule === 'xinwenke'" class="category-tabs">
        <el-tabs v-model="activeTab" @tab-click="handleTabClick">
          <el-tab-pane label="类别管理" name="categories">
            <!-- 类别列表 -->
            <div class="category-list-section">
              <el-table 
                :data="categories" 
                v-loading="loading"
                stripe
                border
                style="width: 100%"
                @selection-change="handleCategorySelectionChange"
                ref="categoryTable"
              >
                <!-- 选择列 -->
                <el-table-column 
                  type="selection" 
                  width="55" 
                  align="center"
                />
                
                <!-- 分类ID -->
                <el-table-column 
                  prop="id" 
                  label="ID" 
                  width="80" 
                  align="center" 
                />
                
                <!-- 类别名称 -->
                <el-table-column 
                  prop="name" 
                  label="类别名称" 
                  width="150" 
                  align="center" 
                />
                
                <!-- 类别编码 -->
                <el-table-column 
                  prop="code" 
                  label="类别编码" 
                  width="150" 
                  align="center" 
                />
                
                <!-- 类别描述 -->
                <el-table-column 
                  prop="description" 
                  label="描述" 
                  min-width="200" 
                  show-overflow-tooltip
                  align="center" 
                />
                
                <!-- 排序 -->
                <el-table-column 
                  prop="sortOrder" 
                  label="排序" 
                  width="100" 
                  align="center" 
                />
                
                <!-- 状态 -->
                <el-table-column 
                  prop="status" 
                  label="状态" 
                  width="100" 
                  align="center"
                >
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
                      {{ scope.row.status === 1 ? '启用' : '禁用' }}
                    </el-tag>
                  </template>
                </el-table-column>
                
                <!-- 创建时间 -->
                <el-table-column 
                  prop="createTime" 
                  label="创建时间" 
                  width="180" 
                  align="center"
                >
                  <template slot-scope="scope">
                    {{ formatDate(scope.row.createTime) }}
                  </template>
                </el-table-column>

                <!-- 操作列 -->
                <el-table-column label="操作" min-width="200" fixed="right" align="center">
                  <template slot-scope="scope">
                    <div class="action-buttons">
                      <el-button 
                        size="mini" 
                        type="warning" 
                        @click="editCategory(scope.row)"
                        v-if="canFileManage"
                        class="action-btn"
                      >
                        修改
                      </el-button>
                      <el-button 
                        size="mini" 
                        type="danger" 
                        @click="deleteCategory(scope.row)"
                        v-if="canFileManage"
                        class="action-btn"
                      >
                        删除
                      </el-button>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-tab-pane>
          
          <el-tab-pane label="类型管理" name="subcategories">
            <!-- 类型列表 -->
            <div class="subcategory-list-section">
              <el-table 
                :data="subcategories" 
                v-loading="loading"
                stripe
                border
                style="width: 100%"
                @selection-change="handleSubcategorySelectionChange"
                ref="subcategoryTable"
              >
                <!-- 选择列 -->
                <el-table-column 
                  type="selection" 
                  width="55" 
                  align="center"
                />
                
                <!-- 类型ID -->
                <el-table-column 
                  prop="id" 
                  label="ID" 
                  width="80" 
                  align="center" 
                />
                
                <!-- 所属类别 -->
                <el-table-column 
                  prop="categoryName" 
                  label="所属类别" 
                  width="120" 
                  align="center" 
                />
                
                <!-- 类型名称 -->
                <el-table-column 
                  prop="name" 
                  label="类型名称" 
                  width="150" 
                  align="center" 
                />
                
                <!-- 类型编码 -->
                <el-table-column 
                  prop="code" 
                  label="类型编码" 
                  width="150" 
                  align="center" 
                />
                
                <!-- 类型描述 -->
                <el-table-column 
                  prop="description" 
                  label="描述" 
                  min-width="200" 
                  show-overflow-tooltip
                  align="center" 
                />
                
                <!-- 排序 -->
                <el-table-column 
                  prop="sortOrder" 
                  label="排序" 
                  width="100" 
                  align="center" 
                />
                
                <!-- 状态 -->
                <el-table-column 
                  prop="status" 
                  label="状态" 
                  width="100" 
                  align="center"
                >
                  <template slot-scope="scope">
                    <el-tag :type="scope.row.status === 1 ? 'success' : 'danger'">
                      {{ scope.row.status === 1 ? '启用' : '禁用' }}
                    </el-tag>
                  </template>
                </el-table-column>
                
                <!-- 创建时间 -->
                <el-table-column 
                  prop="createTime" 
                  label="创建时间" 
                  width="180" 
                  align="center"
                >
                  <template slot-scope="scope">
                    {{ formatDate(scope.row.createTime) }}
                  </template>
                </el-table-column>

                <!-- 操作列 -->
                <el-table-column label="操作" min-width="200" fixed="right" align="center">
                  <template slot-scope="scope">
                    <div class="action-buttons">
                      <el-button 
                        size="mini" 
                        type="warning" 
                        @click="editSubcategory(scope.row)"
                        v-if="canFileManage"
                        class="action-btn"
                      >
                        修改
                      </el-button>
                      <el-button 
                        size="mini" 
                        type="danger" 
                        @click="deleteSubcategory(scope.row)"
                        v-if="canFileManage"
                        class="action-btn"
                      >
                        删除
                      </el-button>
                    </div>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- 其他模块：标签管理 -->
      <div v-else class="tag-management-section">
        <!-- 标签类型切换 -->
        <div class="tag-type-tabs" v-if="availableTagTypes.length > 0">
          <el-tabs v-model="activeTagType" @tab-click="handleTagTypeClick">
            <el-tab-pane 
              v-for="tagType in availableTagTypes" 
              :key="tagType.value"
              :label="tagType.label" 
              :name="tagType.value"
            >
              <!-- 标签列表 -->
              <div class="tag-list-section">
                <el-table 
                  :data="tagList" 
                  :key="`tag-table-${currentModule}-${activeTagType}`"
                  v-loading="tagLoading"
                  stripe
                  border
                  style="width: 100%"
                  @selection-change="handleTagSelectionChange"
                  ref="tagTable"
                >
                  <!-- 选择列 -->
                  <el-table-column 
                    type="selection" 
                    width="55" 
                    align="center"
                  />
                  
                  <!-- 标签ID -->
                  <el-table-column 
                    prop="id" 
                    label="ID" 
                    width="80" 
                    align="center" 
                  />

                  <!-- 标签名称 -->
                  <el-table-column 
                    prop="name" 
                    label="标签名称" 
                    min-width="150" 
                    align="center" 
                  />
                  
                  <!-- 排序 -->
                  <el-table-column 
                    prop="sequ" 
                    label="排序" 
                    width="100" 
                    align="center" 
                  />
                  
                  <!-- 创建时间 -->
                  <el-table-column 
                    prop="source-tag/list" 
                    label="创建时间" 
                    width="180" 
                    align="center"
                  >
                    <template slot-scope="scope">
                      {{ formatDate(scope.row.createTime) }}
                    </template>
                  </el-table-column>
                  
                  <!-- 更新时间 -->
                  <el-table-column 
                    prop="updateTime" 
                    label="更新时间" 
                    width="180" 
                    align="center"
                  >
                    <template slot-scope="scope">
                      {{ formatDate(scope.row.updateTime) }}
                    </template>
                  </el-table-column>

                  <!-- 操作列 -->
                  <el-table-column label="操作" min-width="200" fixed="right" align="center">
                    <template slot-scope="scope">
                      <div class="action-buttons">
                        <el-button 
                          size="mini" 
                          type="warning" 
                          @click="editTag(scope.row)"
                          v-if="canFileManage"
                          class="action-btn"
                        >
                          修改
                        </el-button>
                        <el-button 
                          size="mini" 
                          type="danger" 
                          @click="deleteTag(scope.row)"
                          v-if="canFileManage"
                          class="action-btn"
                        >
                          删除
                        </el-button>
                      </div>
                    </template>
                  </el-table-column>
                </el-table>
                
                <!-- 分页 -->
                <div class="tag-pagination-container">
                  <el-pagination
                    @size-change="handleTagSizeChange"
                    @current-change="handleTagCurrentChange"
                    :current-page="tagPagination.pageNum"
                    :page-sizes="[10, 20, 50, 100]"
                    :page-size="tagPagination.pageSize"
                    :total="tagPagination.total"
                    layout="total, sizes, prev, pager, next, jumper"
                  />
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
        <!-- 无可用标签类型提示 -->
        <div v-else class="no-tag-types-tip">
          <el-alert
            title="提示"
            type="info"
            :closable="false"
            show-icon
          >
            <div slot="description">
              <p>当前模块暂无可用的标签类型，请联系管理员配置。</p>
            </div>
          </el-alert>
        </div>
      </div>
    </div>

    <!-- 新增/编辑类别对话框 -->
    <el-dialog
      :title="categoryDialogTitle"
      :visible.sync="categoryDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      @close="onCategoryDialogClose"
      class="category-dialog"
    >
      <el-form 
        :model="categoryForm" 
        :rules="categoryRules" 
        ref="categoryForm"
        label-width="100px"
      >
        <el-form-item label="类别名称" prop="name">
          <el-input 
            v-model="categoryForm.name" 
            placeholder="请输入类别名称"
            clearable
          />
        </el-form-item>
        
        <el-form-item label="类别编码" prop="code">
          <el-input 
            v-model="categoryForm.code" 
            placeholder="请输入类别编码"
            clearable
          />
        </el-form-item>
        
        <el-form-item label="类别描述" prop="description">
          <el-input 
            v-model="categoryForm.description" 
            type="textarea"
            :rows="3"
            placeholder="请输入类别描述"
          />
        </el-form-item>
        
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number 
            v-model="categoryForm.sortOrder" 
            :min="1"
            :max="999"
            placeholder="排序"
          />
        </el-form-item>
        
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="categoryForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="onCategoryDialogClose">取消</el-button>
        <el-button 
          type="primary" 
          @click="saveCategory"
          :loading="saving"
        >
          确定
        </el-button>
      </div>
    </el-dialog>

    <!-- 新增/编辑类型对话框 -->
    <el-dialog
      :title="subcategoryDialogTitle"
      :visible.sync="subcategoryDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      @close="onSubcategoryDialogClose"
      class="subcategory-dialog"
    >
      <el-form 
        :model="subcategoryForm" 
        :rules="subcategoryRules" 
        ref="subcategoryForm"
        label-width="100px"
      >
        <el-form-item label="所属类别" prop="categoryId">
          <el-select 
            v-model="subcategoryForm.categoryId" 
            placeholder="请选择类别"
            clearable
            style="width: 100%"
          >
            <el-option
              v-for="category in categories"
              :key="category.id"
              :label="category.name"
              :value="category.id"
            />
          </el-select>
        </el-form-item>
        
        <el-form-item label="类型名称" prop="name">
          <el-input 
            v-model="subcategoryForm.name" 
            placeholder="请输入类型名称"
            clearable
          />
        </el-form-item>
        
        <el-form-item label="类型编码" prop="code">
          <el-input 
            v-model="subcategoryForm.code" 
            placeholder="请输入类型编码"
            clearable
          />
        </el-form-item>
        
        <el-form-item label="类型描述" prop="description">
          <el-input 
            v-model="subcategoryForm.description" 
            type="textarea"
            :rows="3"
            placeholder="请输入类型描述"
          />
        </el-form-item>
        
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number 
            v-model="subcategoryForm.sortOrder" 
            :min="1"
            :max="999"
            placeholder="排序"
          />
        </el-form-item>
        
        <el-form-item label="状态" prop="status">
          <el-radio-group v-model="subcategoryForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="onSubcategoryDialogClose">取消</el-button>
        <el-button 
          type="primary" 
          @click="saveSubcategory"
          :loading="saving"
        >
          确定
        </el-button>
      </div>
    </el-dialog>

    <!-- 新增/编辑标签对话框 -->
    <el-dialog
      :title="tagDialogTitle"
      :visible.sync="tagDialogVisible"
      width="600px"
      :close-on-click-modal="false"
      @close="onTagDialogClose"
      class="tag-dialog"
    >
      <el-form 
        :model="tagForm" 
        :rules="tagRules" 
        ref="tagForm"
        label-width="100px"
      >
        <el-form-item label="模块" prop="mark">
          <el-select 
            v-model="tagForm.mark" 
            placeholder="请选择模块"
            style="width: 100%"
            @change="onTagModuleChange"
          >
            <el-option
              v-for="option in moduleOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="标签类型" prop="tag">
          <div class="tag-type-mode">
            <el-radio-group v-model="tagTypeMode" size="small" @change="onTagTypeModeChange">
              <el-radio-button label="select">选择</el-radio-button>
              <el-radio-button label="custom">自定义</el-radio-button>
            </el-radio-group>
          </div>
          <div v-if="tagTypeMode === 'select'" class="tag-type-select">
            <el-select 
              v-model="tagForm.tag" 
              placeholder="请选择标签类型"
              style="width: 100%"
            >
              <el-option
                v-for="option in tagTypeOptions"
                :key="option.value"
                :label="option.label"
                :value="parseInt(option.value)"
              />
            </el-select>
          </div>
          <div v-else class="tag-type-custom">
            <el-input-number
              v-model="tagForm.tag"
              :min="-128"
              :max="127"
              :step="1"
              controls-position="right"
              style="width: 100%"
              placeholder="请输入整数类型值，如 8"
            />
            <p class="tag-type-hint">可自定义整数类型，例如 8 表示画风类别</p>
          </div>
          <div v-if="usedTagValues.length" class="tag-used-values">
            <div class="tag-used-values__label">
              已使用的类型值（{{ usedTagValues.length }}）
            </div>
            <div class="tag-used-values__chips">
              <span 
                class="tag-chip" 
                :class="{ 'tag-chip--current': tagForm.tag === item.tag }"
                v-for="item in usedTagValues" 
                :key="`used-tag-${item.tag}`"
              >
                <span v-if="item.name">
                  {{ item.tag }}（{{ item.name }}）
                </span>
                <span v-else>
                  {{ item.tag }}
                </span>
              </span>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="标签名称" prop="name">
          <el-input 
            v-model="tagForm.name" 
            placeholder="请输入标签名称"
            clearable
          />
        </el-form-item>
        
        <el-form-item label="排序" prop="sequ">
          <el-input-number 
            v-model="tagForm.sequ" 
            :min="1"
            :max="9999"
            placeholder="排序"
          />
        </el-form-item>
      </el-form>
      
      <div slot="footer" class="dialog-footer">
        <el-button @click="onTagDialogClose">取消</el-button>
        <el-button 
          type="primary" 
          @click="saveTag"
          :loading="saving"
        >
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import api from '@/api/api';
import { 
  isAdminLoggedIn, 
  hasAdminPermission, 
  getAdminAuthState,
  adminLogout 
} from '@/utils/adminAuth';

export default {
  name: 'CategoryManagement',
  data() {
    return {
      loading: false,
      saving: false,
      initializing: false, // 防止重复初始化
      currentModule: 'xinwenke', // 当前模块：xinwenke, internet, dachallenge, xiaochallenge, career
      activeTab: 'categories',
      categories: [],
      subcategories: [],
      selectedCategories: [],
      selectedSubcategories: [],
      
      // 标签管理相关
      activeTagType: null, // 当前标签类型
      tagList: [], // 标签列表
      rawTagList: [], // 标签原始列表（用于前端分页）
      moduleTagSummary: {}, // mark -> [{ tag, name }]
      selectedTags: [], // 选中的标签
      tagLoading: false, // 标签加载状态（独立于主loading）
      tagInitLoading: false, // 标签初始化加载状态
      tagInitLoaded: false, // 标签初始化是否已加载
      tagPagination: {
        pageNum: 1,
        pageSize: 20,
        total: 0
      },
      // 所有标签类型定义（根据SQL表注释）
      allTagTypes: [
        { label: '类别', value: '1' },
        { label: '赛道', value: '2' },
        { label: '类型', value: '3' },
        { label: '风格', value: '4' },
        { label: '组别', value: '5' },
        { label: '动画类型', value: '6' },
        { label: '职业方向', value: '0' }
      ],
      // 动态模块标签类型映射：{ moduleKey: [{label, value}] }
      moduleTagTypesMap: {},
      // 默认映射（当接口失败时兜底）
      fallbackModuleTagTypesMap: {
        'internet': ['1', '2', '3', '4', '5'],
        'dachallenge': [],
        'xiaochallenge': [],
        'career': ['0']
      },
      
      // 标签对话框相关
      tagDialogVisible: false,
      tagDialogTitle: '新增标签',
      tagForm: {
        id: null,
        mark: null, // 模块标识：1=创新大赛，2=大挑，3=小挑，4=职业规划
        tag: null, // 标签类型：1=类别，2=赛道，3=类型，4=风格，5=组别，6=动画类型，0=职业方向
        name: '',
        sequ: 1
      },
      tagTypeMode: 'select', // select | custom
      markNameMap: {}, // mark -> markName
      tagRules: {
        mark: [
          { required: true, message: '请选择模块', trigger: 'change' }
        ],
        tag: [
          { 
            required: true, 
            message: '请选择或输入标签类型', 
            trigger: ['change', 'blur'] 
          },
          { 
            validator: (rule, value, callback) => {
              if (value === null || value === undefined || value === '') {
                callback(new Error('请选择或输入标签类型'));
                return;
              }
              // 允许自定义数值，需为整数
              if (!Number.isInteger(value)) {
                callback(new Error('标签类型必须是整数'));
                return;
              }
              callback();
            },
            trigger: ['change', 'blur']
          }
        ],
        name: [
          { required: true, message: '请输入标签名称', trigger: 'blur' },
          { min: 1, max: 64, message: '标签名称长度在 1 到 64 个字符', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              // 防止XSS和特殊字符注入
              if (value && /<script|javascript:|on\w+\s*=|data:|vbscript:/i.test(value)) {
                callback(new Error('标签名称包含非法字符'));
              } else if (value && /[<>'"&]/.test(value)) {
                callback(new Error('标签名称不能包含特殊字符 < > " \' &'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ],
        sequ: [
          { required: true, message: '请输入排序', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              // 确保排序是正整数
              if (value && (!Number.isInteger(value) || value < 1 || value > 9999)) {
                callback(new Error('排序必须是1-9999之间的整数'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ]
      },
      
      // 类别对话框相关
      categoryDialogVisible: false,
      categoryDialogTitle: '新增类别',
      categoryForm: {
        id: null,
        name: '',
        code: '',
        description: '',
        sortOrder: 1,
        status: 1
      },
      categoryRules: {
        name: [
          { required: true, message: '请输入类别名称', trigger: 'blur' },
          { min: 1, max: 50, message: '类别名称长度在 1 到 50 个字符', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              // 防止XSS和特殊字符注入
              if (value && /<script|javascript:|on\w+\s*=|data:|vbscript:/i.test(value)) {
                callback(new Error('类别名称包含非法字符'));
              } else if (value && /[<>'"&]/.test(value)) {
                callback(new Error('类别名称不能包含特殊字符 < > " \' &'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ],
        code: [
          { required: true, message: '请输入类别编码', trigger: 'blur' },
          { min: 1, max: 50, message: '类别编码长度在 1 到 50 个字符', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              // 只允许字母、数字、下划线、连字符
              if (value && !/^[a-zA-Z0-9_-]+$/.test(value)) {
                callback(new Error('类别编码只能包含字母、数字、下划线和连字符'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ],
        description: [
          { max: 200, message: '描述长度不能超过 200 个字符', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              // 防止XSS攻击
              if (value && /<script|javascript:|on\w+\s*=|data:|vbscript:/i.test(value)) {
                callback(new Error('描述包含非法字符'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ],
        sortOrder: [
          { required: true, message: '请输入排序', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              // 确保排序是正整数
              if (value && (!Number.isInteger(value) || value < 1 || value > 999)) {
                callback(new Error('排序必须是1-999之间的整数'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ],
        status: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      },
      
      // 类型对话框相关
      subcategoryDialogVisible: false,
      subcategoryDialogTitle: '新增类型',
      subcategoryForm: {
        id: null,
        categoryId: null,
        name: '',
        code: '',
        description: '',
        sortOrder: 1,
        status: 1
      },
      subcategoryRules: {
        categoryId: [
          { required: true, message: '请选择所属类别', trigger: 'change' },
          { 
            validator: (rule, value, callback) => {
              // 确保categoryId是正整数
              if (value && (!Number.isInteger(value) || value < 1)) {
                callback(new Error('请选择有效的类别'));
              } else {
                callback();
              }
            }, 
            trigger: 'change' 
          }
        ],
        name: [
          { required: true, message: '请输入类型名称', trigger: 'blur' },
          { min: 1, max: 50, message: '类型名称长度在 1 到 50 个字符', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              // 防止XSS和特殊字符注入
              if (value && /<script|javascript:|on\w+\s*=|data:|vbscript:/i.test(value)) {
                callback(new Error('类型名称包含非法字符'));
              } else if (value && /[<>'"&]/.test(value)) {
                callback(new Error('类型名称不能包含特殊字符 < > " \' &'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ],
        code: [
          { required: true, message: '请输入类型编码', trigger: 'blur' },
          { min: 1, max: 50, message: '类型编码长度在 1 到 50 个字符', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              // 只允许字母、数字、下划线、连字符
              if (value && !/^[a-zA-Z0-9_-]+$/.test(value)) {
                callback(new Error('类型编码只能包含字母、数字、下划线和连字符'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ],
        description: [
          { max: 200, message: '描述长度不能超过 200 个字符', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              // 防止XSS攻击
              if (value && /<script|javascript:|on\w+\s*=|data:|vbscript:/i.test(value)) {
                callback(new Error('描述包含非法字符'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ],
        sortOrder: [
          { required: true, message: '请输入排序', trigger: 'blur' },
          { 
            validator: (rule, value, callback) => {
              // 确保排序是正整数
              if (value && (!Number.isInteger(value) || value < 1 || value > 999)) {
                callback(new Error('排序必须是1-999之间的整数'));
              } else {
                callback();
              }
            }, 
            trigger: 'blur' 
          }
        ],
        status: [
          { required: true, message: '请选择状态', trigger: 'change' }
        ]
      }
    };
  },
  computed: {
    // 检查管理员登录状态
    isAdminLoggedIn() {
      return isAdminLoggedIn();
    },
    // 检查文件管理权限
    canFileManage() {
      return hasAdminPermission('canFileManage');
    },
    // 获取当前模块可用的标签类型
    availableTagTypes() {
      if (this.currentModule === 'xinwenke') {
        return [];
      }
      const dynamicTypes = this.moduleTagTypesMap[this.currentModule];
      if (dynamicTypes && dynamicTypes.length > 0) {
        return dynamicTypes;
      }
      const fallbackValues = this.fallbackModuleTagTypesMap[this.currentModule] || [];
      return this.allTagTypes.filter(tagType => fallbackValues.includes(tagType.value));
    },
    // 模块下拉选项
    moduleOptions() {
      const keys = new Set([
        ...Object.keys(this.fallbackModuleTagTypesMap),
        ...Object.keys(this.moduleTagTypesMap)
      ]);
      const labelMap = {
        'internet': '创新大赛',
        'dachallenge': '大挑',
        'xiaochallenge': '小挑',
        'career': '职业大赛'
      };
      return Array.from(keys).map(key => {
        const mark = this.getCurrentModuleMarkFromKey(key);
        const label = this.markNameMap[mark] || labelMap[key] || key;
        return { label, value: mark };
      }).filter(item => item.value);
    },
    // 标签类型下拉选项（基于选定模块）
    tagTypeOptions() {
      if (!this.tagForm.mark) return [];
      const moduleKey = this.getModuleKeyByMark(this.tagForm.mark);
      if (!moduleKey) return [];
      const types = this.moduleTagTypesMap[moduleKey];
      if (types && types.length > 0) return types;
      const fallbackValues = this.fallbackModuleTagTypesMap[moduleKey] || [];
      return this.allTagTypes.filter(tagType => fallbackValues.includes(tagType.value));
    },
      // 已使用的标签类型值（同一模块下）
    usedTagValues() {
        if (!this.tagForm.mark) return [];
        const values = this.moduleTagSummary[this.tagForm.mark];
        // 统一返回 [{ tag, name }] 结构，避免直接渲染为 JSON / [object Object]
        if (!Array.isArray(values)) return [];
        return values.map(item => {
          if (typeof item === 'number') {
            return { tag: item, name: '' };
          }
          if (item && typeof item === 'object') {
            return {
              tag: Number(item.tag),
              name: item.name || item.tagName || ''
            };
          }
          return { tag: Number(item) || 0, name: '' };
        }).filter(item => !Number.isNaN(item.tag));
    }
  },
  mounted() {
    // 初始化activeTagType
    if (this.currentModule !== 'xinwenke') {
      const availableTypes = this.moduleTagTypesMap[this.currentModule] || [];
      this.activeTagType = availableTypes.length > 0 ? availableTypes[0] : null;
    }
    
    // 使用nextTick确保DOM完全渲染后再加载数据
    this.$nextTick(() => {
      this.checkPermissionAndLoadData();
    });
  },
  methods: {
    // 检查权限并加载数据
    async checkPermissionAndLoadData() {
      // 防止重复初始化
      if (this.initializing) {
        return;
      }
      
      this.initializing = true;
      
      try {
        // 检查管理员登录状态
        if (!this.isAdminLoggedIn) {
          this.$message.warning('请先登录管理员账号');
          return;
        }

        // 检查文件管理权限
        if (!this.canFileManage) {
          this.$message.warning('您没有文件管理权限');
          return;
        }

        // 预先拉取标签初始化数据（非新文科模块需要）
        if (this.currentModule !== 'xinwenke') {
          await this.ensureTagInitData();
          await this.ensureModuleTagSummary(this.getCurrentModuleMark());
        }

        // 加载数据
        await this.loadData();
      } finally {
        this.initializing = false;
      }
    },

    // 前往管理员登录页
    goToAdminLogin() {
      this.$router.push({ name: 'AdminLogin' });
    },

    // 管理员登出
    async handleLogout() {
      try {
        await this.$confirm('确定要退出管理员登录吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });
        
        await adminLogout();
        this.$message.success('已退出管理员登录');
        this.$router.push({ name: 'AdminLogin' });
      } catch (error) {
        if (error !== 'cancel') {
          // 登出失败，静默处理
          this.$message.error('登出失败');
        }
      }
    },

    // 切换模块
    switchModule(module) {
      this.currentModule = module;
      // 重置标签类型为第一个可用的标签类型
      const resetTagType = () => {
        if (module !== 'xinwenke') {
          const availableTypes = this.availableTagTypes;
          this.activeTagType = availableTypes.length > 0 ? availableTypes[0].value : null;
          // 重置标签分页
          this.tagPagination.pageNum = 1;
          this.tagPagination.total = 0;
        } else {
          this.activeTagType = null;
        }
      };

      const afterModuleReady = async () => {
        resetTagType();
        const mark = this.getCurrentModuleMark();
        if (module !== 'xinwenke' && mark) {
          // 强制刷新模块标签摘要（不使用缓存）
          await this.refreshModuleTagSummary(mark);
          // 如果标签对话框是打开的，更新tagForm.mark以便usedTagValues计算属性能正确获取新模块的数据
          if (this.tagDialogVisible && this.tagForm.mark !== mark) {
            this.tagForm.mark = mark;
            // 强制刷新模块标签摘要（不使用缓存）
            await this.refreshModuleTagSummary(mark);
            // 更新标签类型选项
            this.$nextTick(() => {
              if (this.tagTypeMode === 'select') {
                const types = this.tagTypeOptions;
                this.tagForm.tag = types.length > 0 ? parseInt(types[0].value) : null;
              }
            });
          }
        }
        this.loadData();
      };

      if (module !== 'xinwenke' && !this.tagInitLoaded) {
        this.ensureTagInitData().then(() => {
          afterModuleReady();
        });
      } else {
        afterModuleReady();
      }
    },

    // 加载所有数据
    async loadData() {
      this.loading = true;
      try {
        if (this.currentModule === 'xinwenke') {
          // 新文科模块：加载分类数据
          // 先加载类别，再加载类型以便映射名称
          await this.loadCategories();
          await this.loadSubcategories();
        } else {
          // 其他模块：加载标签数据
          await this.loadTags();
        }
      } catch (error) {
        // 加载数据失败，静默处理
        this.$message.error('加载数据失败');
      } finally {
        this.loading = false;
      }
    },

    // 加载类别数据
    async loadCategories() {
      try {
        const response = await api.getCategories();
        if (response.data && response.data.code === 200) {
          this.categories = response.data.data || [];
        } else {
          this.$message.error(response.data?.message || '加载类别失败');
        }
      } catch (error) {
        // 加载类别失败，静默处理
        this.$message.error('加载类别失败');
      }
    },

    // 加载类型数据
    async loadSubcategories() {
      try {
        const response = await api.getSubcategories();
        if (response.data && response.data.code === 200) {
          const list = response.data.data || [];
          // 将 categoryId 映射为 categoryName，便于表格展示
          const categoryMap = this.categories.reduce((acc, cur) => {
            acc[cur.id] = cur.name;
            return acc;
          }, {});
          this.subcategories = list.map(item => ({
            ...item,
            categoryName: categoryMap[item.categoryId] || '-'
          }));
        } else {
          this.$message.error(response.data?.message || '加载类型失败');
        }
      } catch (error) {
        // 加载类型失败，静默处理
        this.$message.error('加载类型失败');
      }
    },

    // 标签页切换
    handleTabClick(tab) {
      this.activeTab = tab.name;
    },

    // 新文科新增入口
    handleXinwenkeCreate(command) {
      if (command === 'category') {
        this.showAddCategoryDialog();
      } else if (command === 'type') {
        this.showAddSubcategoryDialog();
      }
    },

    // 类别选择变化
    handleCategorySelectionChange(selection) {
      this.selectedCategories = selection;
    },

    // 类型选择变化
    handleSubcategorySelectionChange(selection) {
      this.selectedSubcategories = selection;
    },

    // 显示新增类别对话框
    showAddCategoryDialog() {
      this.categoryDialogTitle = '新增类别';
      this.categoryForm = {
        id: null,
        name: '',
        code: '',
        description: '',
        sortOrder: 1,
        status: 1
      };
      this.categoryDialogVisible = true;
    },

    // 显示新增类型对话框
    showAddSubcategoryDialog() {
      this.subcategoryDialogTitle = '新增类型';
      this.subcategoryForm = {
        id: null,
        categoryId: null,
        name: '',
        code: '',
        description: '',
        sortOrder: 1,
        status: 1
      };
      this.subcategoryDialogVisible = true;
    },

    // 编辑类别
    editCategory(category) {
      this.categoryDialogTitle = '编辑类别';
      this.categoryForm = {
        id: category.id,
        name: category.name,
        code: category.code,
        description: category.description,
        sortOrder: category.sortOrder,
        status: category.status
      };
      this.categoryDialogVisible = true;
    },

    // 编辑类型
    editSubcategory(subcategory) {
      this.subcategoryDialogTitle = '编辑类型';
      this.subcategoryForm = {
        id: subcategory.id,
        categoryId: subcategory.categoryId,
        name: subcategory.name,
        code: subcategory.code,
        description: subcategory.description,
        sortOrder: subcategory.sortOrder,
        status: subcategory.status
      };
      this.subcategoryDialogVisible = true;
    },

    // 数据清理和转义
    sanitizeInput(input) {
      if (typeof input !== 'string') return input;
      return input
        .replace(/[<>'"&]/g, '') // 移除危险字符
        .trim() // 去除首尾空格
        .substring(0, 200); // 限制长度
    },

    // 保存类别
    async saveCategory() {
      try {
        await this.$refs.categoryForm.validate();
        
        // 数据清理和验证
        const sanitizedData = {
          ...this.categoryForm,
          name: this.sanitizeInput(this.categoryForm.name),
          code: this.sanitizeInput(this.categoryForm.code),
          description: this.sanitizeInput(this.categoryForm.description),
          sortOrder: Math.max(1, Math.min(999, parseInt(this.categoryForm.sortOrder) || 1)),
          status: this.categoryForm.status === 1 ? 1 : 0
        };
        
        this.saving = true;
        let response;
        
        if (sanitizedData.id) {
          // 更新
          response = await api.updateCategory(sanitizedData);
        } else {
          // 新增
          response = await api.createCategory(sanitizedData);
        }
        
        if (response.data && response.data.code === 200) {
          this.$message.success(sanitizedData.id ? '更新成功' : '创建成功');
          this.categoryDialogVisible = false;
          await this.loadCategories();
        } else {
          this.$message.error(response.data?.message || '操作失败');
        }
      } catch (error) {
        if (error !== false) { // 不是表单验证错误
          // 保存类别失败，静默处理
          this.$message.error('保存失败');
        }
      } finally {
        this.saving = false;
      }
    },

    // 保存类型
    async saveSubcategory() {
      try {
        await this.$refs.subcategoryForm.validate();
        
        // 数据清理和验证
        const sanitizedData = {
          ...this.subcategoryForm,
          categoryId: Math.max(1, parseInt(this.subcategoryForm.categoryId) || 1),
          name: this.sanitizeInput(this.subcategoryForm.name),
          code: this.sanitizeInput(this.subcategoryForm.code),
          description: this.sanitizeInput(this.subcategoryForm.description),
          sortOrder: Math.max(1, Math.min(999, parseInt(this.subcategoryForm.sortOrder) || 1)),
          status: this.subcategoryForm.status === 1 ? 1 : 0
        };
        
        this.saving = true;
        let response;
        
        if (sanitizedData.id) {
          // 更新
          response = await api.updateSubcategory(sanitizedData);
        } else {
          // 新增
          response = await api.createSubcategory(sanitizedData);
        }
        
        if (response.data && response.data.code === 200) {
          this.$message.success(sanitizedData.id ? '更新成功' : '创建成功');
          this.subcategoryDialogVisible = false;
          await this.loadSubcategories();
        } else {
          this.$message.error(response.data?.message || '操作失败');
        }
      } catch (error) {
        if (error !== false) { // 不是表单验证错误
          // 保存类型失败，静默处理
          this.$message.error('保存失败');
        }
      } finally {
        this.saving = false;
      }
    },

    // 删除类别
    deleteCategory(category) {
      this.$confirm(`确定要删除类别"${category.name}"吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await api.deleteCategory(category.id);
          if (response.data && response.data.code === 200) {
            this.$message.success('删除成功');
            await this.loadCategories();
          } else {
            this.$message.error(response.data?.message || '删除失败');
          }
        } catch (error) {
          // 删除类别失败，静默处理
          this.$message.error('删除失败');
        }
      }).catch(() => {
        // 用户取消删除
      });
    },

    // 删除类型
    deleteSubcategory(subcategory) {
      this.$confirm(`确定要删除类型"${subcategory.name}"吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await api.deleteSubcategory(subcategory.id);
          if (response.data && response.data.code === 200) {
            this.$message.success('删除成功');
            await this.loadSubcategories();
          } else {
            this.$message.error(response.data?.message || '删除失败');
          }
        } catch (error) {
          // 删除类型失败，静默处理
          this.$message.error('删除失败');
        }
      }).catch(() => {
        // 用户取消删除
      });
    },

    // 类别对话框关闭
    onCategoryDialogClose() {
      this.categoryDialogVisible = false;
      this.categoryForm = {
        id: null,
        name: '',
        code: '',
        description: '',
        sortOrder: 1,
        status: 1
      };
      this.$refs.categoryForm && this.$refs.categoryForm.resetFields();
    },

    // 类型对话框关闭
    onSubcategoryDialogClose() {
      this.subcategoryDialogVisible = false;
      this.subcategoryForm = {
        id: null,
        categoryId: null,
        name: '',
        code: '',
        description: '',
        sortOrder: 1,
        status: 1
      };
      this.$refs.subcategoryForm && this.$refs.subcategoryForm.resetFields();
    },

    // ==================== 标签管理相关方法 ====================
    
    // 获取当前模块的mark值
    getCurrentModuleMark() {
      const markMap = {
        'internet': 1,
        'dachallenge': 2,
        'xiaochallenge': 3,
        'career': 4
      };
      return markMap[this.currentModule] || null;
    },

    // 根据模块key获取mark
    getCurrentModuleMarkFromKey(key) {
      const markMap = {
        'internet': 1,
        'dachallenge': 2,
        'xiaochallenge': 3,
        'career': 4
      };
      return markMap[key] || null;
    },

    // 根据mark获取模块key
    getModuleKeyByMark(mark) {
      const map = {
        1: 'internet',
        2: 'dachallenge',
        3: 'xiaochallenge',
        4: 'career'
      };
      return map[mark] || null;
    },

    // 确保标签初始化数据
    async ensureTagInitData() {
      if (this.tagInitLoaded) return;
      if (this.tagInitLoading) return;
      this.tagInitLoading = true;
      try {
        const res = await api.getTagInitData();
        if (res.data && res.data.code === 200) {
          const list = res.data.data || [];
          const map = {};
          list.forEach(item => {
            const moduleKey = this.getModuleKeyByMark(item.mark);
            if (!moduleKey) return;
            const tags = (item.tagList || []).map(t => ({
              label: t.tagName || String(t.tag),
              value: String(t.tag)
            }));
            map[moduleKey] = tags;
            this.markNameMap[item.mark] = item.markName || item.mark;
          });
          this.moduleTagTypesMap = map;
          this.tagInitLoaded = true;
        } else {
          this.$message.error(res.data?.message || '获取标签初始化数据失败');
        }
      } catch (error) {
        this.$message.error('获取标签初始化数据失败');
      } finally {
        this.tagInitLoading = false;
      }
    },

    // 获取模块标签取值列表（强制刷新）
    async refreshModuleTagSummary(mark) {
      if (!mark) return;
      try {
        const res = await api.getTagList({ mark });
        if (res.data && res.data.code === 200) {
          const list = (res.data.data || [])
            .filter(item => item.tag !== null && item.tag !== undefined)
            .map(item => ({
              tag: Number(item.tag),
              name: item.tagName || item.name || ''
            }));
          const dedupMap = {};
          list.forEach(it => {
            if (dedupMap[it.tag] === undefined) {
              dedupMap[it.tag] = it.name;
            }
          });
          const merged = Object.keys(dedupMap)
            .map(k => Number(k))
            .sort((a, b) => a - b)
            .map(tag => ({ tag, name: dedupMap[tag] }));
          this.$set(this.moduleTagSummary, mark, merged);
        }
      } catch (error) {
        // 静默处理
      }
    },

    // 确保模块标签取值列表
    async ensureModuleTagSummary(mark) {
      if (!mark) return;
      if (this.moduleTagSummary[mark]) return;
      await this.refreshModuleTagSummary(mark);
    },

    // 基于当前raw列表刷新缓存（合并数据，不覆盖）
    updateModuleTagSummaryFromList(mark, list = []) {
      if (!mark) return;
      // 获取现有的摘要数据
      const existing = this.moduleTagSummary[mark] || [];
      const existingMap = {};
      existing.forEach(item => {
        const tag = Number(item.tag);
        if (!Number.isNaN(tag)) {
          existingMap[tag] = item.name || '';
        }
      });
      
      // 合并新数据
      list.forEach(item => {
        if (item.tag === null || item.tag === undefined) return;
        const tag = Number(item.tag);
        if (!Number.isNaN(tag)) {
          // 如果已有数据，保留原有名称；如果没有，使用新名称
          if (existingMap[tag] === undefined) {
            existingMap[tag] = item.tagName || item.name || '';
          }
        }
      });
      
      // 转换为数组并排序
      const merged = Object.keys(existingMap)
        .map(k => Number(k))
        .sort((a, b) => a - b)
        .map(tag => ({ tag, name: existingMap[tag] }));
      this.$set(this.moduleTagSummary, mark, merged);
    },

    // 标签类型切换
    handleTagTypeClick(tab) {
      // 先清空数据和loading状态，避免显示旧数据
      this.tagList = [];
      this.tagLoading = false;
      this.activeTagType = tab.name;
      // 重置分页
      this.tagPagination.pageNum = 1;
      // 使用 nextTick 确保 DOM 更新后再加载数据
      this.$nextTick(() => {
        this.loadTags();
      });
    },

    // 模块选择变化时，重置标签类型
    onTagModuleChange() {
      if (this.tagTypeMode === 'select') {
        const types = this.tagTypeOptions;
        this.tagForm.tag = types.length > 0 ? parseInt(types[0].value) : null;
      }
      this.ensureModuleTagSummary(this.tagForm.mark);
    },

    // 标签类型模式切换
    onTagTypeModeChange(mode) {
      if (mode === 'select') {
        const types = this.tagTypeOptions;
        this.tagForm.tag = types.length > 0 ? parseInt(types[0].value) : null;
      } else {
        this.tagForm.tag = null;
      }
    },

    // 根据现有数值判断选择模式
    setTagTypeModeByValue(mark, tag) {
      const moduleKey = this.getModuleKeyByMark(mark);
      if (!moduleKey) {
        this.tagTypeMode = 'custom';
        return;
      }
      const options = this.moduleTagTypesMap[moduleKey] || [];
      const fallbackValues = this.fallbackModuleTagTypesMap[moduleKey] || [];
      const matched = options.some(opt => parseInt(opt.value) === Number(tag)) ||
        fallbackValues.some(val => Number(val) === Number(tag));
      this.tagTypeMode = matched ? 'select' : 'custom';
    },

    // 加载标签数据（预留接口调用位置）
    async loadTags() {
      if (!this.activeTagType) {
        this.tagList = [];
        this.tagLoading = false;
        this.tagPagination.total = 0;
        this.rawTagList = [];
        return;
      }
      
      const mark = this.getCurrentModuleMark();
      const tag = parseInt(this.activeTagType);
      
      if (!mark) {
        this.$message.error('模块标识错误');
        return;
      }
      
      // 延迟显示 loading，避免快速切换时的闪烁
      let loadingTimer = null;
      const showLoading = () => {
        loadingTimer = setTimeout(() => {
          this.tagLoading = true;
        }, 200);
      };
      
      showLoading();
      
      try {
        const response = await api.getTagList({ mark, tag });
        if (response.data && response.data.code === 200) {
          const data = response.data.data || [];
          this.rawTagList = data;
          this.tagPagination.total = data.length;
          this.refreshTagTable();
          this.updateModuleTagSummaryFromList(mark, data);
        } else {
          this.$message.error(response.data?.message || '加载标签失败');
        }
        
      } catch (error) {
        this.$message.error('加载标签失败');
      } finally {
        // 清除 loading 定时器
        if (loadingTimer) {
          clearTimeout(loadingTimer);
        }
        this.tagLoading = false;
      }
    },

    // 刷新标签表格的当前页数据（前端分页）
    refreshTagTable() {
      const start = (this.tagPagination.pageNum - 1) * this.tagPagination.pageSize;
      const end = start + this.tagPagination.pageSize;
      this.tagList = this.rawTagList.slice(start, end);
    },

    // 标签选择变化
    handleTagSelectionChange(selection) {
      this.selectedTags = selection;
    },

    // 标签分页大小变化
    handleTagSizeChange(val) {
      this.tagPagination.pageSize = val;
      this.tagPagination.pageNum = 1;
      this.refreshTagTable();
    },

    // 标签当前页变化
    handleTagCurrentChange(val) {
      this.tagPagination.pageNum = val;
      this.refreshTagTable();
    },

    // 显示新增标签对话框
    async showAddTagDialog() {
      const mark = this.getCurrentModuleMark();
      const availableTypes = this.availableTagTypes;
      
      // 优先使用当前列表选中的标签类型（activeTagType）
      let selectedTag = null;
      if (this.activeTagType) {
        selectedTag = parseInt(this.activeTagType);
      } else if (availableTypes.length > 0) {
        // 如果没有选中的类型，使用第一个可用的类型
        selectedTag = parseInt(availableTypes[0].value);
      }

      this.tagDialogTitle = '新增标签';
      this.tagForm = {
        id: null,
        mark: mark,
        tag: selectedTag,
        name: '',
        sequ: 1
      };
      
      // 根据选中的标签类型判断模式
      if (selectedTag !== null) {
        this.setTagTypeModeByValue(mark, selectedTag);
      } else {
        this.tagTypeMode = 'custom';
      }
      
      if (mark) {
        // 强制刷新摘要数据，确保显示所有已使用的类型值
        await this.refreshModuleTagSummary(mark);
      }
      this.tagDialogVisible = true;
    },

    // 编辑标签
    async editTag(tag) {
      this.tagDialogTitle = '编辑标签';
      this.tagForm = {
        id: tag.id,
        mark: tag.mark,
        tag: tag.tag,
        name: tag.name,
        sequ: tag.sequ
      };
      this.setTagTypeModeByValue(tag.mark, tag.tag);
      if (tag.mark) {
        // 强制刷新摘要数据，确保显示所有已使用的类型值
        await this.refreshModuleTagSummary(tag.mark);
      }
      this.tagDialogVisible = true;
    },

    // 保存标签（预留接口调用位置）
    async saveTag() {
      try {
        await this.$refs.tagForm.validate();
        
        // 数据清理和验证
        const sanitizedData = {
          ...this.tagForm,
          name: this.sanitizeInput(this.tagForm.name),
          sequ: Math.max(1, Math.min(9999, parseInt(this.tagForm.sequ) || 1))
        };
        
        this.saving = true;
        
        let response;
        if (sanitizedData.id) {
          response = await api.updateTag(sanitizedData);
        } else {
          response = await api.createTag(sanitizedData);
        }
        
        if (response.data && response.data.code === 200) {
          this.$message.success(sanitizedData.id ? '更新成功' : '创建成功');
          this.tagDialogVisible = false;
          
          // 新增标签时，切换到对应的标签类型并刷新列表
          if (!sanitizedData.id) {
            this.tagPagination.pageNum = 1;
            // 如果新增的标签类型与当前选中的类型不同，切换到新增的类型
            if (String(sanitizedData.tag) !== this.activeTagType) {
              this.activeTagType = String(sanitizedData.tag);
            }
          }
          // 刷新标签列表
          await this.loadTags();
          // 刷新摘要数据，确保显示所有已使用的类型值
          if (sanitizedData.mark) {
            await this.refreshModuleTagSummary(sanitizedData.mark);
          }
        } else {
          this.$message.error(response.data?.message || '操作失败');
        }
        
      } catch (error) {
        if (error !== false) { // 不是表单验证错误
          this.$message.error('保存失败');
        }
      } finally {
        this.saving = false;
      }
    },

    // 删除标签（预留接口调用位置）
    deleteTag(tag) {
      this.$confirm(`确定要删除标签"${tag.name}"吗？`, '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await api.deleteTag(tag.id);
          if (response.data && response.data.code === 200) {
            this.$message.success('删除成功');
            // 如果当前页只有一条且不是第一页，删除后回退一页
            if (this.tagList.length === 1 && this.tagPagination.pageNum > 1) {
              this.tagPagination.pageNum -= 1;
            }
            // 刷新标签列表
            await this.loadTags();
            // 刷新摘要数据，确保显示所有已使用的类型值
            if (tag.mark) {
              await this.refreshModuleTagSummary(tag.mark);
            }
          } else {
            this.$message.error(response.data?.message || '删除失败');
          }
          
        } catch (error) {
          this.$message.error('删除失败');
        }
      }).catch(() => {
        // 用户取消删除
      });
    },

    // 标签对话框关闭
    onTagDialogClose() {
      this.tagDialogVisible = false;
      const mark = this.getCurrentModuleMark();
      const availableTypes = this.availableTagTypes;
      const tag = availableTypes.length > 0 ? parseInt(availableTypes[0].value) : null;
      this.tagForm = {
        id: null,
        mark: mark,
        tag: tag,
        name: '',
        sequ: 1
      };
      this.tagTypeMode = 'select';
      this.$refs.tagForm && this.$refs.tagForm.resetFields();
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-CN');
    }
  }
};
</script>

<style scoped>
.category-management-container {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.management-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f0f0f0;
}

.management-header h2 {
  margin: 0;
  color: #333;
  font-size: 24px;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.module-tabs {
  margin-bottom: 20px;
  padding: 15px 0;
  border-bottom: 1px solid #f0f0f0;
}

.module-tabs .el-button-group {
  display: flex;
  gap: 0;
}

.module-tabs .el-button {
  border-radius: 0;
  margin-right: 0;
}

.module-tabs .el-button:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.module-tabs .el-button:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

.category-tabs {
  margin-bottom: 20px;
}

.tag-management-section {
  margin-bottom: 20px;
}

.tag-type-tabs {
  margin-bottom: 20px;
}

.tag-list-section {
  margin-bottom: 20px;
}

.tag-pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.no-tag-types-tip {
  margin-top: 40px;
  padding: 20px;
}

.category-list-section,
.subcategory-list-section {
  margin-bottom: 20px;
}

.category-list-section .el-table,
.subcategory-list-section .el-table {
  table-layout: auto;
  width: 100%;
}

.category-list-section .el-table th,
.subcategory-list-section .el-table th {
  text-align: center;
  background-color: #f5f7fa;
  font-weight: 600;
}

.category-list-section .el-table td,
.subcategory-list-section .el-table td {
  text-align: center;
  vertical-align: middle;
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
  flex-wrap: nowrap;
  white-space: nowrap;
  width: 100%;
  padding: 0 4px;
}

.action-btn {
  min-width: 45px;
  padding: 8px 8px;
  font-size: 11px;
  line-height: 1.2;
  margin: 0;
  flex-shrink: 0;
}

.action-btn + .action-btn {
  margin-left: 1px;
}

.tag-list-section .el-table {
  table-layout: auto;
  width: 100%;
}

.tag-list-section .el-table th {
  text-align: center;
  background-color: #f5f7fa;
  font-weight: 600;
}

.tag-list-section .el-table td {
  text-align: center;
  vertical-align: middle;
}

.tag-type-mode {
  margin-bottom: 8px;
}

.tag-type-hint {
  margin: 6px 0 0;
  color: #999;
  font-size: 12px;
}

/* 新文科新增下拉按钮样式，保持与其他模块按钮一致 */
.xinwenke-add-dropdown .el-button {
  height: 32px;
  padding: 7px 12px;
  display: inline-flex;
  align-items: center;
}
.xinwenke-add-dropdown .el-button .el-icon--right {
  margin-left: 4px;
}
.xinwenke-add-dropdown .el-dropdown-menu__item {
  font-size: 13px;
  padding: 6px 14px;
  line-height: 20px;
}

.tag-used-values {
  margin-top: 12px;
  padding: 10px 12px;
  background: #f8faff;
  border: 1px solid #e1e9ff;
  border-radius: 6px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  min-width: 0;
}

.tag-used-values__label {
  font-size: 12px;
  color: #5a607f;
  margin-bottom: 6px;
  word-break: break-word;
  overflow-wrap: break-word;
}

.tag-used-values__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;
  min-width: 0;
}

.tag-chip {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 6px;
  background: #e7efff;
  color: #3a70ff;
  font-size: 12px;
  border: 1px solid #c9d9ff;
  max-width: 100%;
  box-sizing: border-box;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
}

.tag-chip--current {
  background: #3a70ff;
  color: #fff;
  border-color: #2f5dd8;
}

/* 对话框样式 */
.category-dialog .el-dialog,
.subcategory-dialog .el-dialog,
.tag-dialog .el-dialog {
  margin: 0 auto !important;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 8vh;
  left: 50%;
  transform: translateX(-50%);
}

.category-dialog .el-dialog__body,
.subcategory-dialog .el-dialog__body,
.tag-dialog .el-dialog__body {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  max-height: calc(85vh - 100px);
}

.category-dialog .el-dialog__header,
.subcategory-dialog .el-dialog__header,
.tag-dialog .el-dialog__header {
  padding: 15px 20px 10px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.category-dialog .el-dialog__title,
.subcategory-dialog .el-dialog__title,
.tag-dialog .el-dialog__title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.category-dialog .el-dialog__close,
.subcategory-dialog .el-dialog__close,
.tag-dialog .el-dialog__close {
  font-size: 18px;
  color: #999;
}

.category-dialog .el-dialog__close:hover,
.subcategory-dialog .el-dialog__close:hover,
.tag-dialog .el-dialog__close:hover {
  color: #409eff;
}

/* 确保模态框固定在屏幕中央 */
.category-dialog .el-dialog__wrapper,
.subcategory-dialog .el-dialog__wrapper,
.tag-dialog .el-dialog__wrapper {
  position: fixed !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .category-management-container {
    padding: 15px;
  }
  
  .management-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
  
  /* 移动端操作按钮适配 */
  .action-buttons {
    flex-direction: column;
    gap: 3px;
  }
  
  .action-btn {
    width: 100%;
    min-width: auto;
    padding: 3px 6px;
    font-size: 11px;
  }
  
  .action-btn + .action-btn {
    margin-left: 0;
    margin-top: 1px;
  }
  
  /* 移动端对话框适配 */
  .category-dialog .el-dialog,
  .subcategory-dialog .el-dialog,
  .tag-dialog .el-dialog {
    width: 95% !important;
    margin: 2.5vh auto !important;
    max-height: 95vh;
  }
}
</style>
